import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Entry } from '../../lib/api';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function FitBounds({ coords }: { coords: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (coords.length === 1) {
      map.setView(coords[0], 10);
    } else if (coords.length > 1) {
      map.fitBounds(coords, { padding: [24, 24] });
    }
  }, [map, coords]);
  return null;
}

interface OnThisDayMapProps {
  entries: Entry[];
}

export function OnThisDayMap({ entries }: OnThisDayMapProps) {
  const { t } = useTranslation();
  const pinned = entries.filter(
    (e) => e.locationLatitude != null && e.locationLongitude != null
  );

  if (pinned.length === 0) {
    return (
      <div className="flex items-center justify-center h-[240px] border border-paper-300">
        <p className="text-sm text-ink-500">{t('map.noLocations')}</p>
      </div>
    );
  }

  const coords: [number, number][] = pinned.map((e) => [
    e.locationLatitude!,
    e.locationLongitude!,
  ]);

  return (
    <div className="overflow-hidden border border-paper-300" style={{ height: 240 }}>
      <MapContainer
        center={coords[0]}
        zoom={3}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="© OpenStreetMap contributors"
        />
        <FitBounds coords={coords} />
        {pinned.map((entry) => (
          <Marker
            key={entry.id}
            position={[entry.locationLatitude!, entry.locationLongitude!]}
          >
            <Popup>
              <div className="text-sm">
                {entry.locationName && (
                  <p className="font-medium">{entry.locationName}</p>
                )}
                <p className="text-ink-500">
                  {new Date(entry.entryDate).getFullYear()}
                </p>
                <Link
                  to={`/entries/${entry.id}/view`}
                  className="text-primary-600 hover:underline"
                >
                  {entry.title || t('today.untitled')}
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
